// Styles
import styles from './TablePaginationPageButton.module.css';

// Utils
import cn from '../../../utils/cn';

/**
 * Single button used by `TablePaginationPages` to render either:
 *  - a numeric page (clickable, can be marked active)
 *  - an ellipsis placeholder (rendered as a non-clickable disabled button to keep
 *    layout/borders consistent with neighbouring buttons)
 * @param {object} props - Component props
 * @param {number} [props.value] - Page number (required when `isEllipsis` is false)
 * @param {boolean} [props.isActive=false] - Whether the button represents the current page
 * @param {boolean} [props.isEllipsis=false] - Render as an ellipsis (non-clickable)
 * @param {boolean} [props.disabled=false] - Disable the button
 * @param {(value: number) => void} [props.onClick] - Click handler invoked with `value`
 * @param {string} [props.ariaLabel] - Accessible label override
 * @param {'page'|'arrow'} [props.variant='page'] - `arrow` for prev/next icon controls
 * @param {React.ReactNode} props.children - Content to render (page number or "…")
 * @returns {JSX.Element}
 */
function TablePaginationPageButton({
  value,
  isActive = false,
  isEllipsis = false,
  variant = 'page',
  disabled = false,
  onClick,
  ariaLabel,
  children,
}) {
  const handleClick = () => {
    if (!isEllipsis && onClick && typeof value === 'number') {
      onClick(value);
    }
  };

  return (
    <button
      type="button"
      className={cn([
        styles['table-pagination-page-button'],
        variant === 'arrow' && styles['table-pagination-page-button-arrow'],
        isActive && styles['table-pagination-page-button-active'],
        isEllipsis && styles['table-pagination-page-button-ellipsis'],
      ])}
      onClick={handleClick}
      disabled={disabled || isEllipsis}
      aria-current={isActive ? 'page' : undefined}
      aria-hidden={isEllipsis ? true : undefined}
      aria-label={ariaLabel}
      tabIndex={isEllipsis ? -1 : undefined}
    >
      {children}
    </button>
  );
}

export default TablePaginationPageButton;
